import React from 'react';
import { Tooltip, Input } from 'antd';
import { Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Spinner } from '@crc-evans/ui-components';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';
import '../../pages/technical/HeatProfilePage.css';
import './DPRSettingsPipelinesTab.css';
import './DPRSettingsSparesTab.css';

/**
 * Modern spares grid: single header row — title + per-column search (Shipment / Equipment pattern).
 */
const SparesModernDataGrid = ({
  sortedRows,
  isLoading,
  current,
  pageSize,
  sortKey,
  sortDir,
  onToggleSort,
  formatAssignedToCell,
  formatCostCell,
  totalCostDisplay,
  onEdit,
  onDelete,
  equipmentTypeValue,
  assignedToSerialNumberValue,
  equipmentSubCategory,
  sapKitNo,
  sapNumber,
  description,
  safetyStock,
  cost,
  onServerFilterChange,
  setEquipmentTypeValue,
  setAssignedToSerialNumberValue,
  setEquipmentSubCategory,
  setSapKitNo,
  setSapNumber,
  setDescription,
  setSafetyStock,
  setCost,
  localColFilters,
  onLocalColFilterChange,
  renderTextColumnFilterTrigger,
  tableWrapClassName = '',
  tableMaxHeight,
}) => {
  const renderStaticHead = (label, alignLeft = false, cellClass = '') => (
    <div
      className={`dpr-settings-spares-cell dpr-settings-spares-header-cell dpr-settings-spares-cell-header-static${
        alignLeft ? ' dpr-settings-spares-cell--left' : ''
      }${cellClass ? ` ${cellClass}` : ''}`}
    >
      <div className="column-header-with-filter column-header-with-filter--no-search">
        <div
          className={`column-header-text-wrapper dpr-pipelines-heat-title-row${
            alignLeft ? ' dpr-pipelines-heat-title-row--left' : ''
          }`}
        >
          <span className="dpr-pipelines-heat-label">{label}</span>
        </div>
      </div>
    </div>
  );

  const renderSortableHead = (
    label,
    colKey,
    alignLeft,
    heatFacetKey = null,
    searchNode = null,
    { searchWidth = 120 } = {}
  ) => {
    const active = sortKey === colKey;
    const Icon = active && sortDir === 'desc' ? ChevronDown : ChevronUp;
    const filterNode =
      heatFacetKey && typeof renderTextColumnFilterTrigger === 'function'
        ? renderTextColumnFilterTrigger(heatFacetKey)
        : null;
    return (
      <div
        className={`dpr-settings-spares-cell dpr-settings-spares-header-cell${
          alignLeft ? ' dpr-settings-spares-cell--left' : ''
        }${searchWidth === 68 ? ' dpr-settings-spares-header-cell--narrow' : ''}`}
      >
        <div className="column-header-with-filter">
          <div
            className={`column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row${
              alignLeft ? ' dpr-pipelines-heat-title-row--left' : ''
            }`}
          >
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={() => onToggleSort(colKey)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleSort(colKey);
                }
              }}
            >
              {label}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSort(colKey);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleSort(colKey);
                }
              }}
            >
              <Icon
                className={`dpr-settings-spares-sort-icon ${active ? 'dpr-settings-spares-sort-icon-active' : ''}`}
                size={14}
                aria-hidden
              />
            </span>
            {filterNode}
          </div>
          {searchNode ? (
            <div className="header-filter-input user-mgmt-header-search">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                {searchNode}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const serverFilterInput = (value, filterType, setter, searchWidth = 120) => (
    <Input
      placeholder="Search"
      value={value ?? ''}
      onChange={(e) => onServerFilterChange(filterType, e.target.value, setter)}
      onClick={(e) => e.stopPropagation()}
      size="small"
      style={{ width: searchWidth }}
      aria-label={`Filter ${filterType}`}
    />
  );

  const localFilterInput = (field, searchWidth = 120) => (
    <Input
      placeholder="Search"
      value={localColFilters[field] ?? ''}
      onChange={(e) => onLocalColFilterChange(field, e.target.value)}
      onClick={(e) => e.stopPropagation()}
      size="small"
      style={{ width: searchWidth }}
      aria-label={`Filter ${field}`}
    />
  );

  return (
    <div
      className={`dpr-settings-spares-table-shell ${tableWrapClassName}`.trim()}
      style={tableMaxHeight ? { maxHeight: tableMaxHeight } : undefined}
    >
      <div className="dpr-settings-spares-hscroll">
        <div className="dpr-settings-spares-hscroll-inner">
          <div className="dpr-settings-spares-thead">
            <div className="dpr-settings-spares-header-row dpr-settings-spares-header-row--heat">
              <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num dpr-settings-spares-header-cell">
                <div className="column-header-with-filter column-header-with-filter--no-search">
                  <div className="column-header-text-wrapper dpr-pipelines-heat-title-row">
                    <span className="dpr-pipelines-heat-label">#</span>
                  </div>
                </div>
              </div>
              {renderSortableHead(
                'Equipment type',
                'equipmentType',
                true,
                'equipmentType',
                serverFilterInput(equipmentTypeValue, 'equipmentType', setEquipmentTypeValue)
              )}
              {renderSortableHead(
                'Assigned to',
                'assignedToSerialNumber',
                true,
                '__assignedDisplay',
                serverFilterInput(
                  assignedToSerialNumberValue,
                  'assignedToSerialNumber',
                  setAssignedToSerialNumberValue
                )
              )}
              {renderSortableHead(
                'SAP kit no',
                'sapKitNo',
                true,
                'sapKitNo',
                serverFilterInput(sapKitNo, 'sapKitNo', setSapKitNo)
              )}
              {renderSortableHead(
                'SAP number',
                'sapNumber',
                true,
                'sapNumber',
                serverFilterInput(sapNumber, 'sapNumber', setSapNumber)
              )}
              {renderSortableHead(
                'Description',
                'description',
                true,
                'description',
                serverFilterInput(description, 'description', setDescription)
              )}
              {renderSortableHead(
                'Target stock',
                'targetStock',
                false,
                'targetStock',
                serverFilterInput(safetyStock, 'targetStock', setSafetyStock, 68),
                { searchWidth: 68 }
              )}
              {renderSortableHead(
                'Cost',
                'cost',
                false,
                '__costStr',
                serverFilterInput(cost, 'cost', setCost, 68),
                { searchWidth: 68 }
              )}
              {renderSortableHead(
                'Pending',
                'pendingStock',
                false,
                'pendingStock',
                localFilterInput('pendingStock', 68),
                { searchWidth: 68 }
              )}
              {renderSortableHead(
                'Actual',
                'actualStock',
                false,
                'actualStock',
                localFilterInput('actualStock', 68),
                { searchWidth: 68 }
              )}
              {renderSortableHead(
                'Consumed',
                'consumedQty',
                false,
                'consumedQty',
                localFilterInput('consumedQty', 68),
                { searchWidth: 68 }
              )}
              {renderSortableHead(
                'Total cost',
                'totalCost',
                false,
                '__totalCostStr',
                localFilterInput('totalCost', 68),
                { searchWidth: 68 }
              )}
              {renderStaticHead('Pub.', false, 'dpr-settings-spares-cell-pub')}
              {renderStaticHead('Actions', false, 'dpr-settings-spares-cell-actions')}
            </div>
          </div>
          <div className="dpr-settings-spares-body-scroll">
            {isLoading ? (
              <div className="dpr-settings-spares-loading">
                <Spinner size="medium" />
              </div>
            ) : sortedRows.length === 0 ? (
              <div className="dpr-settings-spares-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : (
              <div className="dpr-settings-spares-rows">
                {sortedRows.map((row, index) => (
                  <div key={row.id ?? index} className="dpr-settings-spares-row">
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {(current - 1) * pageSize + index + 1}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.equipmentType}>
                      {row.equipmentType ?? '—'}
                    </div>
                    <div
                      className="dpr-settings-spares-cell dpr-settings-spares-cell--left"
                      title={formatAssignedToCell(row.assignedToSerialNumber)}
                    >
                      {formatAssignedToCell(row.assignedToSerialNumber)}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.sapKitNo}>
                      {row.sapKitNo ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.sapNumber}>
                      {row.sapNumber ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--left" title={row.description}>
                      {row.description ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.targetStock ?? '—'}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">{formatCostCell(row)}</div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.pendingStock ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.actualStock ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {row.consumedQty ?? 0}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num">
                      {totalCostDisplay(row)}
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell--num dpr-settings-spares-cell-pub">
                      <Tooltip title={row.isPublished ? 'Published' : 'Not published'}>
                        <span className="dpr-settings-spares-published-wrap">
                          <span
                            className={`dpr-settings-spares-published-dot ${
                              row.isPublished
                                ? 'dpr-settings-spares-published-dot--yes'
                                : 'dpr-settings-spares-published-dot--no'
                            }`}
                          />
                        </span>
                      </Tooltip>
                    </div>
                    <div className="dpr-settings-spares-cell dpr-settings-spares-cell-actions">
                      <button
                        type="button"
                        className="dpr-settings-spares-action-btn"
                        onClick={() => onEdit(row)}
                        title="Edit"
                        aria-label="Edit spare"
                      >
                        <Pencil size={18} strokeWidth={1.75} />
                      </button>
                      <button
                        type="button"
                        className="dpr-settings-spares-action-btn dpr-settings-spares-action-delete"
                        onClick={() => onDelete(row)}
                        title="Delete"
                        aria-label="Delete spare"
                      >
                        <Trash2 size={18} strokeWidth={1.75} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SparesModernDataGrid;
